// Animation state lives in refs written during render, which React Compiler cannot memoize safely.
"use no memo";

import { useEffect, useRef, useState } from "react";
import {
  buildMeshCircles,
  buildMeshPalette,
  buildMeshSquares,
  buildTickerColors,
  CircleMeshSvg,
  randomMeshColor,
  shuffleMeshColors,
} from "./CircleMeshSvg";

export function CreditsMesh() {
  // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  const seed = useRef(Math.random().toString());
  const restrictedColors = useRef(shuffleMeshColors());
  const mouseTarget = useRef({ x: 0.5, y: 0.5 });
  const focalLayers = useRef([
    { x: 0.5, y: 0.5 },
    { x: 0.5, y: 0.5 },
    { x: 0.5, y: 0.5 },
  ]);
  // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  const [focalSnapshot, setFocalSnapshot] = useState(focalLayers.current);
  const animating = useRef(false);

  useEffect(() => {
    let frame = 0;
    const rates = [0.08, 0.04, 0.02];
    const tick = () => {
      const layers = focalLayers.current;
      const target = mouseTarget.current;
      let moved = false;
      for (let index = 0; index < layers.length; index++) {
        const layer = layers[index]!;
        const rate = rates[index]!;
        const x = layer.x + (target.x - layer.x) * rate;
        const y = layer.y + (target.y - layer.y) * rate;
        if (Math.abs(x - layer.x) > 0.0001 || Math.abs(y - layer.y) > 0.0001) {
          moved = true;
        }
        layers[index] = { x, y };
      }
      if (moved) {
        setFocalSnapshot([...layers]);
        frame = requestAnimationFrame(tick);
      } else {
        animating.current = false;
      }
    };
    const handleMouseMove = (event: MouseEvent) => {
      mouseTarget.current = {
        x: event.clientX / window.innerWidth,
        y: event.clientY / window.innerHeight,
      };
      if (!animating.current) {
        animating.current = true;
        frame = requestAnimationFrame(tick);
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <CircleMeshSvg
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      seed={seed.current}
      targetCircleSize={110}
      mutingOpacity={0.15}
      build={({ rows, columns, random }) => {
        const palette = buildMeshPalette(
          random,
          rows * columns,
          [400, 600, 800],
          restrictedColors.current
        );
        const getColor = () => randomMeshColor(random, palette);
        const noise = Array.from({ length: rows }, () =>
          Array.from({ length: columns }, () => (random() - 0.5) * 3)
        );
        const muteThreshold =
          Math.sqrt((columns / 2) ** 2 + (rows / 2) ** 2) * 0.13;
        const isMuted = (row: number, col: number) => {
          const offset = noise[row]?.[col] ?? 0;
          const layer = focalSnapshot[((Math.abs(offset) * 1000) | 0) % 3]!;
          const x = col - layer.x * columns;
          const y = row - (1 - layer.y) * rows;
          return Math.sqrt(x * x + y * y) + offset > muteThreshold;
        };

        const { circles } = buildMeshCircles(rows, columns, (row, col) => {
          const hasTicker = random() < 0.5;
          const tickerColors = buildTickerColors(getColor);
          return {
            color: getColor(),
            tickerColors: hasTicker ? tickerColors : [],
            muted: isMuted(row, col),
          };
        });
        const squareVisuals = new Map<
          string,
          { color: string; tickerColors: string[] }
        >();
        const squares = buildMeshSquares(
          rows,
          columns,
          (row, col, adjacentCells) => {
            const key = `${row}-${col}`;
            let visual = squareVisuals.get(key);
            if (!visual) {
              visual = {
                color: getColor(),
                tickerColors: buildTickerColors(getColor),
              };
              squareVisuals.set(key, visual);
            }
            return {
              ...visual,
              muted: adjacentCells.some(([cellRow, cellCol]) =>
                isMuted(cellRow, cellCol)
              ),
            };
          }
        );
        return { circles, squares };
      }}
    />
  );
}
